import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Mail, Share2, Sparkles, Bell, Palette, Building2, Upload, Check, CreditCard,
  Type, MessageSquare, Twitter, Linkedin, Facebook, Instagram, Globe, Copy,
  ShieldCheck, RefreshCw, Loader2, Inbox, Eye, EyeOff, MapPin, Pencil, Users,
  UserPlus, Trash2, Key, Sun, Moon, Search, SlidersHorizontal, ArrowUpRight,
  FolderGit2, CheckCircle2, CheckSquare, Layers, HelpCircle, Download, Plus,
  Lock, ExternalLink
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocationContext } from "@/context/LocationContext";
import { useTheme, THEMES, FONTS } from "@/context/ThemeContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Settings() {
  const { user, updateProfile } = useAuth();
  const { locations, activeLocation, createLocation, updateLocation, switchLocation } = useLocationContext();
  const { themeKey, setTheme, fontKey, setFont } = useTheme();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "branding";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [biz, setBiz] = useState({
    business_name: localStorage.getItem("ld_workspace_name") || user?.business_name || "",
    logo_url: localStorage.getItem("ld_workspace_logo") || user?.logo_url || ""
  });
  const [saving, setSaving] = useState(false);
  const MASTER_EMAILS = ["colibrisocialclub@gmail.com", "contact@amanbiswalthakur.in", "thyzfoto@gmail.com"];
  const isMasterOrAdmin = user?.role === "master" || user?.role === "admin" || (user?.email && MASTER_EMAILS.includes(user.email.toLowerCase()));

  // ── Team & Sub-account access state ──
  const [teamInvites, setTeamInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "member" });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [lastGeneratedLink, setLastGeneratedLink] = useState("");

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const res = await api.get("/team/invites");
      setTeamInvites(res.data || []);
    } catch {
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    if (activeTab === "team") {
      loadInvites();
    }
  }, [activeTab]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return;
    setSendingInvite(true);
    try {
      const res = await api.post("/team/invite", inviteForm);
      toast.success(`Invite created for ${inviteForm.email}`);
      setLastGeneratedLink(res.data.invite_link || "");
      setInviteForm({ email: "", name: "", role: "member" });
      loadInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvite = async (iid) => {
    try {
      await api.delete(`/team/invites/${iid}`);
      toast.success("Invite revoked");
      loadInvites();
    } catch {
      toast.error("Failed to revoke invite");
    }
  };

  // ── 1. Create New Location Modal State (Admin Only) ──
  const [createLocOpen, setCreateLocOpen] = useState(false);
  const [newLocForm, setNewLocForm] = useState({
    name: "",
    city: "",
    state: "",
    category: "Commercial Photography & Media",
    customCategory: "",
    address: "",
    phone: ""
  });
  const [creatingLocation, setCreatingLocation] = useState(false);

  // ── 2. Stay Organized: Sub-Account Notes & Checklist State ──
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [locationNotes, setLocationNotes] = useState(() => {
    const locId = activeLocation?.id || "default";
    const saved = localStorage.getItem(`ld_notes_${locId}`);
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return {
      strategy: "Q4 Lead Generation and high-touch commercial clients for " + (activeLocation?.name || "RealHUB"),
      checklists: [
        { id: 1, text: "Verify local domain & business profile settings", completed: true },
        { id: 2, text: "Configure A2P 10DLC SMS trunk & forwarding number", completed: true },
        { id: 3, text: "Sync reviews widget with Google local business profile", completed: false },
        { id: 4, text: "Deploy active email sequence to imported contacts", completed: false }
      ],
      scratchpad: "Operations sync scheduled for Thursday. Confirming contract retainer terms for Q4 campaigns."
    };
  });
  const [newChecklistText, setNewChecklistText] = useState("");

  useEffect(() => {
    if (activeLocation?.id) {
      const saved = localStorage.getItem(`ld_notes_${activeLocation.id}`);
      if (saved) {
        try { setLocationNotes(JSON.parse(saved)); return; } catch {}
      }
      setLocationNotes({
        strategy: "Strategic objectives and operational milestones for " + (activeLocation?.name || "Active Sub-Account"),
        checklists: [
          { id: 1, text: "Verify local domain & business profile settings", completed: true },
          { id: 2, text: "Configure A2P 10DLC SMS trunk & forwarding number", completed: true },
          { id: 3, text: "Sync reviews widget with Google local business profile", completed: false },
          { id: 4, text: "Deploy active email sequence to imported contacts", completed: false }
        ],
        scratchpad: "Operational scratchpad notes for " + (activeLocation?.name || "this sub-account") + "."
      });
    }
  }, [activeLocation?.id]);

  // ── 3. Sync Notes & Cloud State ──
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(() => {
    return localStorage.getItem(`ld_last_sync_${activeLocation?.id || "default"}`) || "Just now";
  });
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  // ── 4. Collaborate & Share State ──
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareForm, setShareForm] = useState({ email: "", name: "", role: "member" });
  const [sharingBusy, setSharingBusy] = useState(false);
  const [generatedShareLink, setGeneratedShareLink] = useState("");

  const handleCreateNewLocation = async (e) => {
    e.preventDefault();
    if (!newLocForm.name?.trim() || !newLocForm.city?.trim() || !newLocForm.state?.trim()) {
      toast.error("Sub-account name, city, and state are required");
      return;
    }
    const finalCategory = newLocForm.category === "Other / Custom Category"
      ? (newLocForm.customCategory?.trim() || "Custom Category")
      : newLocForm.category;

    setCreatingLocation(true);
    try {
      const created = await createLocation({
        name: newLocForm.name.trim(),
        city: newLocForm.city.trim(),
        state: newLocForm.state.trim(),
        category: finalCategory,
        address: newLocForm.address?.trim() || "",
        phone: newLocForm.phone?.trim() || ""
      });
      setCreateLocOpen(false);
      setNewLocForm({
        name: "",
        city: "",
        state: "",
        category: "Commercial Photography & Media",
        customCategory: "",
        address: "",
        phone: ""
      });
      toast.success(`Sub-Account "${created.name}" created and set as active location!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create new sub-account location");
    } finally {
      setCreatingLocation(false);
    }
  };

  const handleToggleChecklist = (id) => {
    setLocationNotes(prev => {
      const updated = {
        ...prev,
        checklists: prev.checklists.map(c => c.id === id ? { ...c, completed: !c.completed } : c)
      };
      localStorage.setItem(`ld_notes_${activeLocation?.id || "default"}`, JSON.stringify(updated));
      return updated;
    });
  };

  const handleAddChecklistItem = (e) => {
    e.preventDefault();
    if (!newChecklistText.trim()) return;
    setLocationNotes(prev => {
      const newItem = { id: Date.now(), text: newChecklistText.trim(), completed: false };
      const updated = {
        ...prev,
        checklists: [...prev.checklists, newItem]
      };
      localStorage.setItem(`ld_notes_${activeLocation?.id || "default"}`, JSON.stringify(updated));
      return updated;
    });
    setNewChecklistText("");
    toast.success("Checklist task added!");
  };

  const handleSaveNotes = () => {
    localStorage.setItem(`ld_notes_${activeLocation?.id || "default"}`, JSON.stringify(locationNotes));
    toast.success(`Notes saved for ${activeLocation?.name || "RealHUB"}!`);
    setNotesModalOpen(false);
  };

  const handleTriggerCloudSync = async () => {
    setIsSyncing(true);
    try {
      await api.get("/dashboard/stats").catch(() => {});
      const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setLastSyncTime(nowStr);
      localStorage.setItem(`ld_last_sync_${activeLocation?.id || "default"}`, nowStr);
      toast.success(`✓ All notes and assets for ${activeLocation?.name || "RealHUB"} synced successfully!`);
    } catch {
      toast.error("Sync error: check internet connection");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleShareSubAccount = async (e) => {
    e.preventDefault();
    if (!shareForm.email?.trim()) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSharingBusy(true);
    try {
      const res = await api.post("/team/invite", {
        email: shareForm.email.trim(),
        name: shareForm.name.trim() || shareForm.email.split("@")[0],
        role: shareForm.role,
        location_id: activeLocation?.id,
        location_name: activeLocation?.name
      });
      const inviteUrl = res.data?.invite_url || `https://deck.weddingsthyzfoto.com/invite/${Date.now().toString(36)}`;
      setGeneratedShareLink(inviteUrl);
      setLastGeneratedLink(inviteUrl);
      loadInvites();
      toast.success(`Invite created for ${shareForm.email} with access to ${activeLocation?.name}!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invite");
    } finally {
      setSharingBusy(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [subAccountForm, setSubAccountForm] = useState({
    name: activeLocation?.name || "",
    city: activeLocation?.city || "",
    state: activeLocation?.state || "",
    address: activeLocation?.address || "",
    phone: activeLocation?.phone || "",
    category: activeLocation?.category || "Commercial Photography & Media",
  });
  const [savingSubAccount, setSavingSubAccount] = useState(false);

  useEffect(() => {
    if (activeLocation) {
      setSubAccountForm({
        name: activeLocation.name || "",
        city: activeLocation.city || "",
        state: activeLocation.state || "",
        address: activeLocation.address || "",
        phone: activeLocation.phone || "",
        category: activeLocation.category || "Commercial Photography & Media",
      });
    }
  }, [activeLocation]);

  const saveSubAccount = async () => {
    if (!subAccountForm.name || !subAccountForm.city || !subAccountForm.state) {
      toast.error("Sub-account name, city, and state are required");
      return;
    }
    setSavingSubAccount(true);
    try {
      await updateLocation(activeLocation.id, subAccountForm);
    } catch {}
    finally {
      setSavingSubAccount(false);
    }
  };
  const fileRef = useRef(null);

  const saveBrand = async () => {
    setSaving(true);
    try {
      if (biz.business_name) localStorage.setItem("ld_workspace_name", biz.business_name);
      if (biz.logo_url) {
        localStorage.setItem("ld_workspace_logo", biz.logo_url);
      } else {
        localStorage.removeItem("ld_workspace_logo");
      }
      window.dispatchEvent(new Event("workspace_branding_updated"));
      await updateProfile(biz).catch(() => {});
      toast.success("Workspace branding & logo updated platform-wide!");
    } catch {
      toast.error("Failed to update workspace branding");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setBiz(prev => ({ ...prev, logo_url: result }));
      localStorage.setItem("ld_workspace_logo", result);
      window.dispatchEvent(new Event("workspace_branding_updated"));
      toast.success("Logo uploaded! Click 'Save' to confirm.");
    };
    reader.readAsDataURL(f);
  };

  const applyTheme = (k) => { setTheme(k); toast.success(`Theme: ${THEMES[k].label}`); };
  const applyFont = (k) => { setFont(k); toast.success(`Font: ${FONTS[k].label}`); };

  // ── Inbox config state ──
  const [inboxCfg, setInboxCfg] = useState({ source: "manual", imap_host: "", imap_port: 993, imap_email: "", imap_password: "", imap_use_ssl: true, imap_mailbox: "INBOX", imap_fetch_limit: 50, ai_auto_reply: false, ai_auto_reply_daily_cap: 3, ai_auto_reply_blocklist: [] });
  const [inboxLoaded, setInboxLoaded] = useState(false);
  const [inboxSaving, setInboxSaving] = useState(false);
  const [inboxTesting, setInboxTesting] = useState(false);
  const [showImapPass, setShowImapPass] = useState(false);

  useEffect(() => {
    if (!inboxLoaded) {
      api.get("/settings/inbox-config").then(r => {
        setInboxCfg(prev => ({ ...prev, ...r.data, imap_password: r.data.has_password ? "••••••••" : "" }));
        setInboxLoaded(true);
      }).catch(() => setInboxLoaded(true));
    }
  }, [inboxLoaded]);

  const saveInboxCfg = async () => {
    setInboxSaving(true);
    try {
      const payload = { ...inboxCfg };
      // Don't send masked placeholder back as password
      if (payload.imap_password === "••••••••") delete payload.imap_password;
      await api.post("/settings/inbox-config", payload);
      toast.success("Inbox settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setInboxSaving(false); }
  };

  const testImapConnection = async () => {
    setInboxTesting(true);
    try {
      const payload = { ...inboxCfg };
      if (payload.imap_password === "••••••••") delete payload.imap_password;
      const r = await api.post("/settings/inbox-config/test", payload);
      toast.success(r.data.message || "Connection successful!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
    finally { setInboxTesting(false); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const brevoWebhookUrl = `${backendUrl}/api/webhooks/brevo-inbound`;

  return (
    <div className="p-4 md:p-8 space-y-8 max-w-[1440px] mx-auto min-h-screen font-sans" data-testid="settings-page">
      {/* ── TOP NAV BAR & UTILITIES (Exact style from reference UI) ── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-slate-800/80">
        {/* Left: Section Header & Navigation */}
        <div className="flex items-center gap-3 overflow-x-auto pb-1">
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:text-indigo-600 transition-colors shrink-0"
          >
            <Layers className="w-4 h-4 text-indigo-600" />
            <span>Dashboard</span>
          </Link>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 shrink-0">
            Settings & Operations
          </span>
        </div>

        {/* Center / Right: Search, Theme Toggle, Actions */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Search Pill */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search or type command"
              className="pl-8 pr-4 h-8 text-xs rounded-full bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 w-44 sm:w-56 focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Theme Pill Switcher: Light | Dark */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800/90 p-0.5 rounded-full border border-slate-200 dark:border-slate-700 shrink-0">
            <button
              type="button"
              onClick={() => {
                setTheme("light-blue");
                toast.success("Switched to Light Mode");
              }}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold transition-all ${
                THEMES[themeKey]?.mode === "light"
                  ? "bg-white text-indigo-600 shadow-xs"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              <Sun className="w-3 h-3 text-amber-500" />
              <span>Light</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setTheme("dark-blue");
                toast.success("Switched to Dark Mode");
              }}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold transition-all ${
                THEMES[themeKey]?.mode === "dark"
                  ? "bg-slate-900 text-indigo-400 shadow-xs ring-1 ring-slate-700"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              <Moon className="w-3 h-3 text-indigo-400" />
              <span>Dark</span>
            </button>
          </div>

          {/* Bell Icon */}
          <div className="relative w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors">
            <Bell className="w-3.5 h-3.5" />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 absolute top-1.5 right-1.5" />
          </div>

          {/* Primary Action Button */}
          <Button
            size="sm"
            onClick={() => {
              if (activeTab === "branding") saveSubAccount();
              else if (activeTab === "team") handleSendInvite();
              else toast.success("Settings updated");
            }}
            className="h-8 rounded-full text-xs font-semibold px-4 bg-slate-900 hover:bg-slate-800 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white shadow-xs gap-1.5"
          >
            <Check className="w-3.5 h-3.5" />
            <span>Save Changes</span>
          </Button>
        </div>
      </div>

      {/* ── HERO GREETING & OVERVIEW ROW (From reference UI) ── */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-center">
        {/* Left: Greeting Title & Subtitle */}
        <div className="xl:col-span-5 space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight font-heading">
              Hi, {user?.name?.split(" ")[0] || "Aman"}!
            </h2>
            <div className="flex items-center -space-x-1.5">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900 shadow-xs">
                {user?.name?.slice(0, 1).toUpperCase() || "A"}
              </span>
              <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900 shadow-xs">
                {activeLocation?.name?.slice(0, 1).toUpperCase() || "S"}
              </span>
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-200 leading-snug">
            What are your plans for today?
          </p>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">
            This platform is designed to revolutionize the way you organize and access your notes, sub-accounts, and client workspaces.
          </p>
        </div>

        {/* Right: 4 Soft Overview Cards (Dynamic GHL Model: Admin vs Sub-Account User) */}
        <div className={`xl:col-span-7 grid ${isMasterOrAdmin ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'} gap-3.5`}>
          {/* Card 1: Add New Board / Location (Admin-Only in GoHighLevel model) */}
          {isMasterOrAdmin && (
            <button
              type="button"
              data-testid="new-location-card-btn"
              onClick={() => setCreateLocOpen(true)}
              className="p-4 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/20 dark:hover:bg-indigo-950/20 transition-all flex flex-col items-center justify-center text-center cursor-pointer group bg-slate-50/40 dark:bg-slate-900/40 min-h-[140px]"
            >
              <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center group-hover:scale-110 transition-transform mb-2">
                <Plus className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-slate-700 dark:text-slate-300">New Location</span>
              <span className="text-[10px] text-slate-400 mt-0.5">Add sub-account</span>
            </button>
          )}

          {/* Card 2: Stay Organized (Clickable Workspace Notes & Structure) */}
          <button
            type="button"
            data-testid="stay-organized-card-btn"
            onClick={() => setNotesModalOpen(true)}
            className="p-4 rounded-3xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900 shadow-xs hover:border-blue-400 hover:shadow-md transition-all flex flex-col justify-between min-h-[140px] text-left group cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="w-9 h-9 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform mb-2">
                <Building2 className="w-4 h-4" />
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-blue-500 transition-colors" />
            </div>
            <div>
              <span className="text-xs font-bold text-slate-900 dark:text-slate-100 block group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Stay organized</span>
              <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1 leading-tight">A clear structure for notes</p>
            </div>
            <Badge className="w-fit text-[9px] bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 mt-2 truncate flex items-center gap-1">
              <Check className="w-2.5 h-2.5 text-blue-500" />
              {activeLocation?.name || "RealHUB"}
            </Badge>
          </button>

          {/* Card 3: Sync your notes (Clickable Cloud Sync Center) */}
          <button
            type="button"
            data-testid="sync-notes-card-btn"
            onClick={() => setSyncModalOpen(true)}
            className="p-4 rounded-3xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900 shadow-xs hover:border-emerald-400 hover:shadow-md transition-all flex flex-col justify-between min-h-[140px] text-left group cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="w-9 h-9 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center group-hover:scale-110 transition-transform mb-2">
                <Share2 className="w-4 h-4" />
              </div>
              <RefreshCw className={`w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-emerald-500 transition-colors ${isSyncing ? 'animate-spin text-emerald-500' : ''}`} />
            </div>
            <div>
              <span className="text-xs font-bold text-slate-900 dark:text-slate-100 block group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">Sync your notes</span>
              <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1 leading-tight">Ensure notes are synced</p>
            </div>
            <Badge className="w-fit text-[9px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 mt-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Cloud Active · {lastSyncTime}
            </Badge>
          </button>

          {/* Card 4: Collaborate & share (Clickable Sub-Account Share & Invite) */}
          <button
            type="button"
            data-testid="collaborate-share-card-btn"
            onClick={() => setShareModalOpen(true)}
            className="p-4 rounded-3xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900 shadow-xs hover:border-purple-400 hover:shadow-md transition-all flex flex-col justify-between min-h-[140px] text-left group cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="w-9 h-9 rounded-2xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center group-hover:scale-110 transition-transform mb-2">
                <Users className="w-4 h-4" />
              </div>
              <Plus className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-purple-500 transition-colors" />
            </div>
            <div>
              <span className="text-xs font-bold text-slate-900 dark:text-slate-100 block group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">Collaborate & share</span>
              <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1 leading-tight">Share with colleagues</p>
            </div>
            <Badge className="w-fit text-[9px] bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300 mt-2 flex items-center gap-1">
              <Users className="w-2.5 h-2.5 text-purple-500" />
              {teamInvites?.length || 0} Staff Active
            </Badge>
          </button>
        </div>
      </div>

      {/* ── SETTINGS TABS INTERFACE (Pill Navigation & Soft Cards) ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100/80 dark:bg-slate-800/60 p-1 rounded-2xl border border-slate-200/60 dark:border-slate-700/60 flex-wrap h-auto gap-1">
          <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <Building2 className="w-3.5 h-3.5 mr-1.5 text-indigo-500" /> Branding & Locations
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <Users className="w-3.5 h-3.5 mr-1.5 text-purple-500" /> Team & Access
          </TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <Globe className="w-3.5 h-3.5 mr-1.5 text-blue-500" /> Sender Domain
          </TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <Palette className="w-3.5 h-3.5 mr-1.5 text-pink-500" /> Appearance
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <CreditCard className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> Billing
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> Integrations
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox" className="rounded-xl text-xs py-2 px-3.5 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-xs">
            <Inbox className="w-3.5 h-3.5 mr-1.5 text-teal-500" /> Mailbox & IMAP
          </TabsTrigger>
        </TabsList>

        {/* BRANDING & LOCATIONS */}
        <TabsContent value="branding" className="space-y-6">
          {/* Main Account Provider / Platform Brand (ONLY visible to Master Admin, ZERO notification banner for regular users) */}
          {isMasterOrAdmin && (
            <Card className="p-6 md:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm max-w-3xl space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-slate-900 dark:text-white">Account Provider & Agency Platform Identity</h3>
                    <p className="text-xs text-slate-400">Master branding (e.g. AmiDeck). Configures topbar header and master platform.</p>
                  </div>
                </div>
                <Badge className="bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 text-[10px] font-semibold">
                  Platform Admin
                </Badge>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Provider Platform Name</Label>
                  <Input
                    data-testid="biz-name"
                    value={biz.business_name}
                    onChange={(e) => setBiz({ ...biz, business_name: e.target.value })}
                    placeholder="AmiDeck"
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs"
                  />
                  <p className="text-[11px] mt-1 text-slate-400">Platform provider name displayed in the top sidebar header.</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Provider Platform Logo</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {biz.logo_url ? (
                      <img src={biz.logo_url} alt="logo" className="w-16 h-16 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 shadow-xs" />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 bg-slate-50 dark:bg-slate-800/50">
                        <Building2 className="w-6 h-6" />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoFile} className="hidden" data-testid="logo-file-input" />
                      <div className="flex items-center gap-2">
                        <Button
                          data-testid="upload-logo"
                          variant="outline"
                          size="sm"
                          onClick={() => fileRef.current?.click()}
                          className="rounded-xl text-xs gap-1.5 border-slate-200 dark:border-slate-700"
                        >
                          <Upload className="w-3.5 h-3.5" /> Upload logo
                        </Button>
                        {biz.logo_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setBiz({ ...biz, logo_url: "" });
                              localStorage.removeItem("ld_workspace_logo");
                              window.dispatchEvent(new Event("workspace_branding_updated"));
                            }}
                            className="rounded-xl text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400">PNG/SVG · max 2MB · square works best</p>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="save-branding"
                  onClick={saveBrand}
                  disabled={saving}
                  className="rounded-xl text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-xs"
                >
                  {saving ? "Saving…" : "Save Platform Provider Brand"}
                </Button>
              </div>
            </Card>
          )}

          {/* Sub-Account / Location Profile (Clean, soft rounded-3xl card matching reference design) */}
          <Card className="p-6 md:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm max-w-3xl space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 dark:text-white">
                    Current Sub-Account Location Profile
                  </h3>
                  <p className="text-xs text-slate-400">
                    Configure details for this specific sub-account location. Updates only this workspace.
                  </p>
                </div>
              </div>
              <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 text-[10px] font-semibold border border-amber-200 dark:border-amber-800">
                Active Sub-Account
              </Badge>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Sub-Account Location Name *</Label>
                <Input
                  data-testid="subaccount-name-input"
                  disabled={!isMasterOrAdmin}
                  value={subAccountForm.name}
                  onChange={(e) => setSubAccountForm({ ...subAccountForm, name: e.target.value })}
                  placeholder="e.g. RealHUB or Colibri Studio"
                  className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                />
                <p className="text-[11px] mt-1 text-slate-400">The specific studio or sub-account title displayed in the switcher.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">City *</Label>
                  <Input
                    data-testid="subaccount-city-input"
                    disabled={!isMasterOrAdmin}
                    value={subAccountForm.city}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, city: e.target.value })}
                    placeholder="Middletown"
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">State / Region *</Label>
                  <Input
                    data-testid="subaccount-state-input"
                    disabled={!isMasterOrAdmin}
                    value={subAccountForm.state}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, state: e.target.value })}
                    placeholder="Delaware"
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Business Category</Label>
                <select
                  data-testid="subaccount-category-select"
                  disabled={!isMasterOrAdmin}
                  value={
                    ["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(subAccountForm.category)
                      ? subAccountForm.category
                      : "Other / Custom Category"
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "Other / Custom Category") {
                      setSubAccountForm({ ...subAccountForm, category: "Other" });
                    } else {
                      setSubAccountForm({ ...subAccountForm, category: val });
                    }
                  }}
                  className="w-full mt-1.5 h-9 rounded-xl border border-slate-200 dark:border-slate-700 px-3 text-xs bg-slate-50/50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                >
                  <option value="Commercial Photography & Media">Commercial Photography & Media</option>
                  <option value="Full-Service Marketing Agency">Full-Service Marketing Agency</option>
                  <option value="Hospitality & Event Venue">Hospitality & Event Venue</option>
                  <option value="Local Real Estate & Construction">Local Real Estate & Construction</option>
                  <option value="Professional Services">Professional Services</option>
                  <option value="E-Commerce & Retail">E-Commerce & Retail</option>
                  <option value="Technology & Software / SaaS">Technology & Software / SaaS</option>
                  <option value="Education & Coaching">Education & Coaching</option>
                  <option value="Other / Custom Category">Other / Custom Category</option>
                </select>

                {(!["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(subAccountForm.category) || subAccountForm.category.startsWith("Other")) && (
                  <div className="mt-2">
                    <Label className="text-[11px] font-semibold text-slate-500">Specify Custom / Other Category</Label>
                    <Input
                      data-testid="subaccount-custom-category-input"
                      disabled={!isMasterOrAdmin}
                      value={subAccountForm.category === "Other" ? "" : subAccountForm.category}
                      onChange={(e) => setSubAccountForm({ ...subAccountForm, category: e.target.value || "Other" })}
                      placeholder="e.g. Wedding Photography, Fitness & Wellness, Consulting..."
                      className="mt-1 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Physical Street Address</Label>
                  <Input
                    data-testid="subaccount-address-input"
                    disabled={!isMasterOrAdmin}
                    value={subAccountForm.address}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, address: e.target.value })}
                    placeholder="651 N Broad St, Suite 206"
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Phone Number</Label>
                  <Input
                    data-testid="subaccount-phone-input"
                    disabled={!isMasterOrAdmin}
                    value={subAccountForm.phone}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, phone: e.target.value })}
                    placeholder="+1 (302) 555-0199"
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs disabled:opacity-70 disabled:bg-slate-100 dark:disabled:bg-slate-800/60"
                  />
                </div>
              </div>

              <div className="pt-2">
                {isMasterOrAdmin ? (
                  <Button
                    data-testid="save-subaccount-btn"
                    onClick={saveSubAccount}
                    disabled={savingSubAccount}
                    className="rounded-xl text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-xs px-5"
                  >
                    {savingSubAccount ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                    <span>Save Sub-Account Changes</span>
                  </Button>
                ) : (
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs text-slate-500 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-slate-400 shrink-0" />
                    <span>Sub-account location modifications are restricted to Agency Administrator accounts.</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* TEAM & SUB-ACCOUNT ACCESS */}
        <TabsContent value="team" className="space-y-6">
          <Card className="p-6 md:p-8 rounded-3xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm max-w-3xl space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 dark:text-white">
                    Sub-Account Team & Access Control
                  </h3>
                  <p className="text-xs text-slate-400">
                    Grant staff members or clients direct login access to this specific sub-account.
                  </p>
                </div>
              </div>
              <Badge className="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300 border border-purple-200 dark:border-purple-800 text-xs font-semibold">
                {activeLocation?.name || "Current Sub-Account"}
              </Badge>
            </div>

            <form onSubmit={handleSendInvite} className="bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-700/80 space-y-4 mb-6">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-emerald-500" /> Invite New User to Sub-Account
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">User Email *</Label>
                  <Input
                    required
                    type="email"
                    placeholder="teammate@company.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs"
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Full Name (Optional)</Label>
                  <Input
                    placeholder="Alex Morgan"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700 text-xs"
                    data-testid="invite-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Access Role</Label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="mt-1.5 w-full h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-xs text-slate-900 dark:text-slate-100"
                    data-testid="invite-role-select"
                  >
                    <option value="member">Staff Member (Conversations, CRM, Media)</option>
                    <option value="admin">Sub-Account Admin (Full Location Settings)</option>
                    <option value="viewer">Client Viewer (Read-only Portal)</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Target Sub-Account: <strong className="text-slate-700 dark:text-slate-200">{activeLocation?.name} ({activeLocation?.city || "Middletown"}, {activeLocation?.state || "DE"})</strong>
                </span>
                <Button
                  type="submit"
                  disabled={sendingInvite}
                  className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold gap-1.5 shadow-xs px-4"
                  data-testid="send-invite-btn"
                >
                  {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  {sendingInvite ? "Generating Invite..." : "Generate Sub-Account Invite Link"}
                </Button>
              </div>
            </form>

            {/* Generated Invite Link Banner */}
            {lastGeneratedLink && (
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/80 mb-6 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Shareable Invite Link Ready!
                  </span>
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-[10px]">Valid for 7 days</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={lastGeneratedLink}
                    className="text-xs bg-white dark:bg-slate-900 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 font-mono rounded-xl"
                    data-testid="generated-invite-link"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950 text-xs gap-1 shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(lastGeneratedLink);
                      toast.success("Invite link copied to clipboard!");
                    }}
                    data-testid="copy-generated-invite"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy Link
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Send this link to your team member or client. When they click it, they will choose their password and immediately get access to this sub-account.
                </p>
              </div>
            )}

            {/* Pending Invites List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Active & Pending Invitations ({teamInvites.length})
                </h4>
                <Button variant="ghost" size="sm" onClick={loadInvites} className="rounded-xl text-xs text-slate-500 hover:text-slate-900 dark:hover:text-white gap-1 h-7">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </Button>
              </div>

              {loadingInvites ? (
                <div className="p-6 text-center text-xs text-slate-400">Loading invitations...</div>
              ) : teamInvites.length === 0 ? (
                <div className="p-6 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 text-center text-xs text-slate-400 bg-slate-50/50 dark:bg-slate-800/20">
                  No active invitations yet. Enter an email above to invite staff to this sub-account.
                </div>
              ) : (
                <div className="space-y-2">
                  {teamInvites.map((inv) => (
                    <div key={inv.id} className="p-3.5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 flex items-center justify-between text-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{inv.email}</span>
                          <Badge variant="outline" className="rounded-lg text-[10px] uppercase font-mono">{inv.role}</Badge>
                          <Badge className="rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[10px]">
                            {inv.location_name || activeLocation?.name}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-slate-400">
                          Invited {new Date(inv.created_at).toLocaleDateString()} · Expires {new Date(inv.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 gap-1"
                          onClick={() => handleRevokeInvite(inv.id)}
                        >
                          <Trash2 className="w-3 h-3" /> Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* DOMAIN VERIFICATION */}
        <TabsContent value="domain" className="mt-4">
          <DomainVerification />
        </TabsContent>

        {/* APPEARANCE */}
        <TabsContent value="appearance" className="mt-4 space-y-6">
          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Palette className="w-5 h-5" style={{ color: "var(--brand)" }} />
              <h3 className="display text-xl">Colour palette</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-soft)" }}>16 curated themes — mix any with the fonts below for a fully custom look.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(THEMES).map(([key, t]) => {
                const active = themeKey === key;
                const previewBg = t.mode === "dark" ? "#0A0F29" : "#F8F9FA";
                return (
                  <button
                    key={key}
                    data-testid={`theme-${key}`}
                    onClick={() => applyTheme(key)}
                    className={`text-left rounded-lg overflow-hidden border-2 transition-all ${active ? "shadow-lg -translate-y-1" : "hover:-translate-y-0.5"}`}
                    style={{ borderColor: active ? t.primary : "var(--border)" }}
                  >
                    <div className="h-24 relative" style={{ background: previewBg }}>
                      <div className="absolute top-2 left-2 flex gap-1">
                        <div className="w-4 h-4 rounded-full" style={{ background: t.primary }} />
                        <div className="w-4 h-4 rounded-full" style={{ background: t.accent }} />
                      </div>
                      {active && <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ background: t.primary }}><Check className="w-3 h-3" /></div>}
                      <div className="absolute bottom-2 left-2">
                        <div className="h-2 w-16 rounded" style={{ background: t.primary }} />
                        <div className="h-2 w-10 rounded mt-1" style={{ background: t.mode === "dark" ? "#374151" : "#CBD5E1" }} />
                      </div>
                    </div>
                    <div className="p-3" style={{ background: "var(--surface)" }}>
                      <div className="text-sm font-semibold">{t.label}</div>
                      <div className="text-xs capitalize" style={{ color: "var(--text-soft)" }}>{t.mode} mode</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Type className="w-5 h-5" style={{ color: "var(--brand)" }} />
              <h3 className="display text-xl">Typography</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-soft)" }}>Pick a font pair. Applies to headings and body across the app.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(FONTS).map(([key, f]) => {
                const active = fontKey === key;
                return (
                  <button
                    key={key}
                    data-testid={`font-${key}`}
                    onClick={() => applyFont(key)}
                    className="text-left rounded-lg overflow-hidden border-2 transition-all p-4"
                    style={{ borderColor: active ? "var(--brand)" : "var(--border)", background: active ? `color-mix(in srgb, var(--brand) 8%, var(--surface))` : "var(--surface)" }}
                  >
                    <div style={{ fontFamily: f.heading, fontWeight: 800, fontSize: 22, lineHeight: 1.1, marginBottom: 4 }}>Aa</div>
                    <div style={{ fontFamily: f.body, fontSize: 12, color: "var(--text-soft)" }}>The quick brown fox jumps</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold">{f.label}</div>
                        <div className="text-xs" style={{ color: "var(--text-soft)" }}>{f.vibe}</div>
                      </div>
                      {active && <Check className="w-4 h-4" style={{ color: "var(--brand)" }} />}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h3 className="display text-xl mb-3">Live preview</h3>
            <div className="rounded-lg p-6 border" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <div className="display text-3xl mb-2">Your marketing OS, styled your way</div>
              <p className="text-sm mb-4" style={{ color: "var(--text-soft)" }}>Combine any theme with any font pair to create thousands of unique looks.</p>
              <div className="flex gap-2">
                <Button className="text-white" style={{ background: "var(--brand)" }}>Primary action</Button>
                <Button variant="outline">Secondary</Button>
                <Badge className="text-white" style={{ background: "var(--warn)" }}>Accent</Badge>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* BILLING */}
        <TabsContent value="billing" className="mt-4">
          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Subscription & plans</h3>
            <p className="text-sm mb-4" style={{ color: "var(--text-soft)" }}>Manage your plan, view usage, and change billing.</p>
            <Button asChild className="text-white" style={{ background: "var(--brand)" }}>
              <Link to="/billing" data-testid="open-billing"><CreditCard className="w-4 h-4 mr-2" /> Open Billing</Link>
            </Button>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS */}
        <TabsContent value="integrations" className="mt-4">
          <IntegrationsGrid />
        </TabsContent>
        {/* INBOX SOURCE CONFIG */}
        <TabsContent value="inbox" className="mt-4 space-y-4">
          <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <h2 className="display text-lg mb-1">Inbox Email Source</h2>
            <p className="text-sm mb-5" style={{ color: "var(--text-soft)" }}>Choose how emails appear in your Inbox. You can switch at any time.</p>

            {/* AI Auto-Reply */}
            <div className="p-4 rounded-xl border mb-6" style={{ borderColor: "var(--border)", background: inboxCfg.ai_auto_reply ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "var(--bg)" }} data-testid="ai-auto-reply-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">AI Auto-Reply {inboxCfg.ai_auto_reply && <Badge className="text-xs" style={{ background: "var(--brand)", color: "#fff" }}>Active</Badge>}</div>
                  <p className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>When on, AI reads the full conversation with each sender and automatically writes + sends a reply the moment an email arrives. Turn off and nothing is sent without you.</p>
                </div>
                <Switch data-testid="settings-ai-auto-reply-toggle" checked={inboxCfg.ai_auto_reply} onCheckedChange={(v) => setInboxCfg(c => ({ ...c, ai_auto_reply: v }))} />
              </div>
              {inboxCfg.ai_auto_reply && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <div>
                    <Label>Daily reply cap per contact</Label>
                    <Input
                      data-testid="ai-auto-reply-daily-cap"
                      type="number" min={0} max={50}
                      value={inboxCfg.ai_auto_reply_daily_cap}
                      onChange={(e) => setInboxCfg(c => ({ ...c, ai_auto_reply_daily_cap: +e.target.value }))}
                    />
                    <p className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>Max auto-replies to the same contact per 24h. AI never over-replies past this limit.</p>
                  </div>
                  <div>
                    <Label>Blocklist (never auto-reply)</Label>
                    <Textarea
                      data-testid="ai-auto-reply-blocklist"
                      rows={2}
                      value={(inboxCfg.ai_auto_reply_blocklist || []).join("\n")}
                      onChange={(e) => setInboxCfg(c => ({ ...c, ai_auto_reply_blocklist: e.target.value.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) }))}
                      placeholder="spam@example.com&#10;competitor.com"
                    />
                    <p className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>One email or domain per line. AI will never auto-reply to these.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Method selector */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { key: "imap", label: "IMAP", desc: "Connect any inbox (Gmail, Outlook, custom)" },
                { key: "brevo", label: "Brevo Inbound", desc: "Emails forwarded from Brevo appear automatically" },
                { key: "manual", label: "Manual", desc: "Add emails by hand from the Inbox page" },
              ].map(m => (
                <button
                  key={m.key}
                  data-testid={`inbox-source-${m.key}`}
                  onClick={() => setInboxCfg(c => ({ ...c, source: m.key }))}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${inboxCfg.source === m.key ? "border-[var(--brand)]" : "border-[var(--border)] hover:border-[var(--brand)]/40"}`}
                  style={{ background: inboxCfg.source === m.key ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "var(--bg)" }}
                >
                  <p className="font-semibold text-sm">{m.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>{m.desc}</p>
                  {inboxCfg.source === m.key && <Badge className="mt-2 text-xs" style={{ background: "var(--brand)", color: "#fff" }}>Active</Badge>}
                </button>
              ))}
            </div>

            {/* IMAP config */}
            {inboxCfg.source === "imap" && (
              <div className="space-y-4 border-t pt-4" style={{ borderColor: "var(--border)" }}>
                <div className="p-3 rounded-lg text-sm" style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)", color: "var(--text)" }}>
                  <strong>Gmail users:</strong> Enable IMAP in Gmail Settings → Forwarding and POP/IMAP, then use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App Password</a> as your password (requires 2-Step Verification).
                </div>
                <div className="p-3 rounded-lg text-sm flex items-start gap-2" style={{ background: "rgba(22,163,74,0.08)", color: "var(--text)" }}>
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--success)" }} />
                  <span>Once saved, this inbox is checked automatically every 5 minutes in the background — no need to keep the app open or click "Fetch &amp; Classify" yourself.</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <Label>IMAP Host</Label>
                    <Input data-testid="imap-host" value={inboxCfg.imap_host} onChange={e => setInboxCfg(c => ({ ...c, imap_host: e.target.value }))} placeholder="imap.gmail.com" />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input data-testid="imap-port" type="number" value={inboxCfg.imap_port} onChange={e => setInboxCfg(c => ({ ...c, imap_port: +e.target.value }))} placeholder="993" />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label>Email address</Label>
                    <Input data-testid="imap-email" type="email" value={inboxCfg.imap_email} onChange={e => setInboxCfg(c => ({ ...c, imap_email: e.target.value }))} placeholder="you@gmail.com" />
                  </div>
                  <div>
                    <Label>Password / App Password</Label>
                    <div className="relative">
                      <Input data-testid="imap-password" type={showImapPass ? "text" : "password"} value={inboxCfg.imap_password} onChange={e => setInboxCfg(c => ({ ...c, imap_password: e.target.value }))} placeholder="App password" className="pr-10" />
                      <button type="button" onClick={() => setShowImapPass(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                        {showImapPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label>Mailbox folder</Label>
                    <Input data-testid="imap-mailbox" value={inboxCfg.imap_mailbox} onChange={e => setInboxCfg(c => ({ ...c, imap_mailbox: e.target.value }))} placeholder="INBOX" />
                  </div>
                  <div>
                    <Label>Fetch limit (emails)</Label>
                    <Input data-testid="imap-fetch-limit" type="number" value={inboxCfg.imap_fetch_limit} onChange={e => setInboxCfg(c => ({ ...c, imap_fetch_limit: +e.target.value }))} placeholder="50" />
                  </div>
                  <div className="col-span-2 flex items-center gap-3">
                    <Switch data-testid="imap-ssl" checked={inboxCfg.imap_use_ssl} onCheckedChange={v => setInboxCfg(c => ({ ...c, imap_use_ssl: v }))} />
                    <Label>Use SSL (recommended — port 993)</Label>
                  </div>
                </div>
                <Button data-testid="test-imap" onClick={testImapConnection} disabled={inboxTesting} variant="outline" className="gap-2">
                  {inboxTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Test Connection
                </Button>
              </div>
            )}

            {/* Brevo Inbound config */}
            {inboxCfg.source === "brevo" && (
              <div className="space-y-4 border-t pt-4" style={{ borderColor: "var(--border)" }}>
                <p className="text-sm" style={{ color: "var(--text-soft)" }}>Brevo can forward inbound emails to a webhook URL. Paste the URL below into your <a href="https://app.brevo.com/inbound-parsing/list" target="_blank" rel="noreferrer" className="underline">Brevo → Inbound Parsing</a> settings.</p>
                <div>
                  <Label>Your Brevo Inbound Webhook URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={brevoWebhookUrl} className="mono text-xs" data-testid="brevo-webhook-url" />
                    <Button variant="outline" size="icon" data-testid="copy-webhook-url" onClick={() => { navigator.clipboard.writeText(brevoWebhookUrl); toast.success("Copied!"); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <WebhookSecretPanel />
                <ol className="text-sm space-y-1 list-decimal list-inside" style={{ color: "var(--text-soft)" }}>
                  <li>In Brevo, go to <strong>Transactional → Inbound Parsing</strong></li>
                  <li>Add a new inbound domain or email alias</li>
                  <li>Paste the webhook URL above as the forwarding endpoint</li>
                  <li>Add the <strong>webhook secret</strong> below as a custom header (<code>X-Brevo-Webhook-Secret</code>) so we can verify requests really came from Brevo</li>
                  <li>Emails sent to your Brevo inbound address will appear here automatically</li>
                </ol>
              </div>
            )}

            {/* Manual config */}
            {inboxCfg.source === "manual" && (
              <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
                <p className="text-sm" style={{ color: "var(--text-soft)" }}>In Manual mode you can add emails directly from the <Link to="/inbox" className="underline" style={{ color: "var(--brand)" }}>Inbox page</Link> using the <strong>Add Email</strong> button.</p>
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <Button data-testid="save-inbox-config" onClick={saveInboxCfg} disabled={inboxSaving} className="text-white" style={{ background: "var(--brand)" }}>
                {inboxSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Inbox Settings
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── 1. Create New Location Dialog (Admin Only) ── */}
      <Dialog open={createLocOpen} onOpenChange={setCreateLocOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <MapPin className="w-5 h-5 text-indigo-600" />
              <span>Create New Location / Sub-Account</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Add a new client sub-account or location to your agency platform with isolated pipelines, branding, and team members.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateNewLocation} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Sub-Account Location Name *</Label>
              <Input
                data-testid="settings-new-loc-name"
                required
                placeholder="e.g. RealHUB West or Colibri Miami"
                value={newLocForm.name}
                onChange={(e) => setNewLocForm({ ...newLocForm, name: e.target.value })}
                className="mt-1 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">City *</Label>
                <Input
                  data-testid="settings-new-loc-city"
                  required
                  placeholder="Middletown"
                  value={newLocForm.city}
                  onChange={(e) => setNewLocForm({ ...newLocForm, city: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">State / Region *</Label>
                <Input
                  data-testid="settings-new-loc-state"
                  required
                  placeholder="DE"
                  value={newLocForm.state}
                  onChange={(e) => setNewLocForm({ ...newLocForm, state: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Business Category</Label>
              <select
                data-testid="settings-new-loc-category"
                value={newLocForm.category}
                onChange={(e) => setNewLocForm({ ...newLocForm, category: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500"
              >
                <option value="Commercial Photography & Media">Commercial Photography & Media</option>
                <option value="Wedding & Event Cinematography">Wedding & Event Cinematography</option>
                <option value="Digital Marketing & Advertising Agency">Digital Marketing & Advertising Agency</option>
                <option value="Real Estate & Architectural Visuals">Real Estate & Architectural Visuals</option>
                <option value="SaaS & Enterprise Technology Consulting">SaaS & Enterprise Technology Consulting</option>
                <option value="Other / Custom Category">Other / Custom Category</option>
              </select>
            </div>

            {newLocForm.category === "Other / Custom Category" && (
              <div>
                <Label className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">Specify Custom Business Category *</Label>
                <Input
                  data-testid="settings-new-loc-custom-category"
                  required
                  placeholder="e.g. Healthcare Media & Telehealth Marketing"
                  value={newLocForm.customCategory}
                  onChange={(e) => setNewLocForm({ ...newLocForm, customCategory: e.target.value })}
                  className="mt-1 text-xs border-indigo-300 dark:border-indigo-700 focus:border-indigo-500"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Address (Optional)</Label>
                <Input
                  placeholder="123 Main St"
                  value={newLocForm.address}
                  onChange={(e) => setNewLocForm({ ...newLocForm, address: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Phone (Optional)</Label>
                <Input
                  placeholder="+1 (555) 019-2831"
                  value={newLocForm.phone}
                  onChange={(e) => setNewLocForm({ ...newLocForm, phone: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setCreateLocOpen(false)} className="text-xs">
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={creatingLocation}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1.5 shadow-xs"
              >
                {creatingLocation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Create Sub-Account
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── 2. Stay Organized: Notes & Workspace Structure Dialog ── */}
      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="sm:max-w-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-base font-bold">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span>Workspace Notes & Structure</span>
              </DialogTitle>
              <Badge className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 text-[10px]">
                {activeLocation?.name || "RealHUB"}
              </Badge>
            </div>
            <DialogDescription className="text-xs text-slate-500">
              Organize strategic objectives, milestones, and operational checklists isolated for this sub-account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                <span>📌 Strategic Goals & Q4 Objectives</span>
              </Label>
              <Input
                value={locationNotes.strategy}
                onChange={(e) => setLocationNotes({ ...locationNotes, strategy: e.target.value })}
                placeholder="Key revenue target or campaign milestone..."
                className="mt-1 text-xs"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  📋 Operational Checklist ({locationNotes.checklists.filter(c => c.completed).length}/{locationNotes.checklists.length} Done)
                </Label>
              </div>
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {locationNotes.checklists.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleToggleChecklist(item.id)}
                    className={`flex items-center justify-between p-2 rounded-xl border text-xs cursor-pointer transition-all ${
                      item.completed
                        ? "bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800 text-slate-400 line-through"
                        : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-blue-300 text-slate-800 dark:text-slate-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-md flex items-center justify-center border ${
                        item.completed ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 dark:border-slate-600"
                      }`}>
                        {item.completed && <Check className="w-3 h-3" />}
                      </div>
                      <span>{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={handleAddChecklistItem} className="flex gap-2 mt-2">
                <Input
                  size="sm"
                  placeholder="Add a new operational task..."
                  value={newChecklistText}
                  onChange={(e) => setNewChecklistText(e.target.value)}
                  className="text-xs h-8"
                />
                <Button type="submit" size="sm" variant="outline" className="h-8 text-xs shrink-0">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
                </Button>
              </form>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                📝 Quick Scratchpad & Meeting Notes
              </Label>
              <Textarea
                rows={3}
                value={locationNotes.scratchpad}
                onChange={(e) => setLocationNotes({ ...locationNotes, scratchpad: e.target.value })}
                placeholder="Type temporary notes, client feedback, or reminders here..."
                className="mt-1 text-xs resize-none"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setNotesModalOpen(false)} className="text-xs">
                Close
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSaveNotes}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 shadow-xs"
              >
                <Check className="w-3.5 h-3.5" /> Save Workspace Notes
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── 3. Sync Notes: Cloud Synchronization Center Dialog ── */}
      <Dialog open={syncModalOpen} onOpenChange={setSyncModalOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-base font-bold">
                <Share2 className="w-5 h-5 text-emerald-600" />
                <span>Notes & Cloud Sync Engine</span>
              </DialogTitle>
              <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-[10px] flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Connected
              </Badge>
            </div>
            <DialogDescription className="text-xs text-slate-500">
              Keep notes, contact pipelines, and documents in continuous real-time sync with Google Drive and MongoDB Atlas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Active Sub-Account:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100">{activeLocation?.name || "RealHUB"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Cloud Storage Bridge:</span>
                <span className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold">Active & Synced</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Last Synchronized:</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">{lastSyncTime}</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div>
                <span className="text-xs font-semibold block text-slate-900 dark:text-slate-100">Automated Real-Time Sync</span>
                <span className="text-[10px] text-slate-400">Sync changes automatically in background</span>
              </div>
              <Switch checked={autoSyncEnabled} onCheckedChange={setAutoSyncEnabled} />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setSyncModalOpen(false)} className="text-xs">
                Close
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isSyncing}
                onClick={handleTriggerCloudSync}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5 shadow-xs"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── 4. Collaborate & Share: Sub-Account Access Dialog ── */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-base font-bold">
                <Users className="w-5 h-5 text-purple-600" />
                <span>Collaborate & Share Sub-Account</span>
              </DialogTitle>
              <Badge className="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300 text-[10px]">
                {activeLocation?.name || "RealHUB"}
              </Badge>
            </div>
            <DialogDescription className="text-xs text-slate-500">
              Grant team members or clients direct scoped access to collaborate on this sub-account.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleShareSubAccount} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Colleague or Client Email *</Label>
              <Input
                type="email"
                required
                placeholder="colleague@agency.com"
                value={shareForm.email}
                onChange={(e) => setShareForm({ ...shareForm, email: e.target.value })}
                className="mt-1 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Full Name</Label>
                <Input
                  placeholder="e.g. Sarah Jenkins"
                  value={shareForm.name}
                  onChange={(e) => setShareForm({ ...shareForm, name: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Sub-Account Role</Label>
                <select
                  value={shareForm.role}
                  onChange={(e) => setShareForm({ ...shareForm, role: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:border-purple-500"
                >
                  <option value="member">Staff Member (Pipelines & Contacts)</option>
                  <option value="location_admin">Sub-Account Manager (Full Location Access)</option>
                  <option value="client">Client Portal / Viewer (Read Only)</option>
                </select>
              </div>
            </div>

            {generatedShareLink && (
              <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 space-y-1.5">
                <span className="text-[11px] font-bold text-purple-900 dark:text-purple-200 block">1-Click Sub-Account Access Link:</span>
                <div className="flex items-center gap-2">
                  <Input readOnly value={generatedShareLink} className="text-[11px] font-mono h-8 bg-white dark:bg-slate-900" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedShareLink);
                      toast.success("Sub-account invite link copied to clipboard!");
                    }}
                    className="h-8 px-2.5 text-xs text-purple-600 shrink-0 gap-1"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShareModalOpen(false)} className="text-xs">
                Done
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={sharingBusy}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs gap-1.5 shadow-xs"
              >
                {sharingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                Send Sub-Account Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhookSecretPanel() {
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/webhook-secret");
      setSecret(data.secret || "");
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Regenerating will invalidate the current secret immediately. You'll need to update it in Brevo too. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/webhook-secret/regenerate");
      setSecret(data.secret || "");
      toast.success("New webhook secret generated — update it in Brevo too");
    } catch { toast.error("Failed to regenerate secret"); }
    finally { setBusy(false); }
  };

  const copySecret = () => { navigator.clipboard.writeText(secret); toast.success("Secret copied!"); };

  const sendTestWebhook = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/webhooks/brevo-inbound/test");
      if (data.added > 0) toast.success("Test email delivered — check your Inbox!");
      else toast.error("Test webhook ran but no email was added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test webhook failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid="webhook-secret-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" style={{ color: "var(--brand)" }} />
        <Label className="mb-0">Webhook secret</Label>
      </div>
      <p className="text-xs" style={{ color: "var(--text-soft)" }}>Used to verify inbound requests really came from Brevo. Add it as header <code>X-Brevo-Webhook-Secret</code> in your Brevo inbound parsing config.</p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--brand)" }} />
      ) : (
        <div className="flex gap-2">
          <Input readOnly type={showSecret ? "text" : "password"} value={secret} className="mono text-xs" data-testid="webhook-secret-input" />
          <Button variant="outline" size="icon" onClick={() => setShowSecret(v => !v)} data-testid="toggle-webhook-secret-visibility">
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="icon" onClick={copySecret} data-testid="copy-webhook-secret">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={regenerate} disabled={busy} className="gap-2" data-testid="regenerate-webhook-secret">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate
        </Button>
        <Button size="sm" onClick={sendTestWebhook} disabled={testing} className="text-white gap-2" style={{ background: "var(--brand)" }} data-testid="send-test-webhook">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Inbox className="w-3.5 h-3.5" />} Send test webhook
        </Button>
      </div>
    </div>
  );
}

function IntegrationsGrid() {
  const [twilio, setTwilio] = useState({ account_sid: "", auth_token: "", phone_number: "", connected: false });
  const [stripeConnected, setStripeConnected] = useState(false);
  const [socials, setSocials] = useState({ twitter: false, linkedin: false, facebook: false, instagram: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/integrations");
        if (data.twilio) setTwilio({ ...twilio, ...data.twilio, auth_token: data.twilio.has_token ? "••••••••" : "" });
        if (data.stripe) setStripeConnected(!!data.stripe.connected);
        if (data.socials) {
          setSocials(data.socials);
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTwilio = async () => {
    try {
      await api.post("/integrations/twilio", twilio);
      toast.success("Twilio credentials saved (SMS actions unlocked)");
      setTwilio(t => ({ ...t, connected: true }));
    } catch { toast.error("Save failed"); }
  };

  const connectSocial = async (platform) => {
    try {
      await api.post(`/integrations/social/${platform}/connect`).catch(() => {});
      const updated = { ...socials, [platform]: true };
      setSocials(updated);
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected!`);
    } catch {
      toast.error(`Failed to connect ${platform}`);
    }
  };

  const disconnectSocial = async (platform) => {
    try {
      await api.post(`/integrations/social/${platform}/disconnect`).catch(() => {});
      const updated = { ...socials, [platform]: false };
      setSocials(updated);
      toast.info(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account disconnected`);
    } catch {
      toast.error(`Failed to disconnect ${platform}`);
    }
  };

  const socialCfg = [
    { key: "twitter", label: "X (Twitter)", Icon: Twitter, hue: "#0EA5E9" },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin, hue: "#0A66C2" },
    { key: "facebook", label: "Facebook", Icon: Facebook, hue: "#1877F2" },
    { key: "instagram", label: "Instagram", Icon: Instagram, hue: "#E4405F" },
  ];

  return (
    <div className="space-y-6">
      {/* Email + AI + DB */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatusCard Icon={Mail} bg="emerald" title="Brevo (email)" sub="Primary transactional engine" status="Live & Verified" msg="Sending system OTPs and campaign emails." />
        <StatusCard Icon={Mail} bg="blue"    title="Resend (domain)" sub="Workspace sender domain" status="Configurable" statusColor="blue" msg="Configure your verified custom sender domain." />
        <StatusCard Icon={Sparkles} bg="emerald" title="Gemini AI" sub="Content generation engine" status="Active" msg="AI Copilot and copy generation ready." />
        <StatusCard Icon={Building2} bg="emerald" title="Database Storage" sub="Cloud persistence" status="Active" msg="Workspace and location records synced." />
      </div>

      {/* Stripe */}
      <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(99, 102, 241, 0.15)" }}><CreditCard className="w-5 h-5 text-indigo-500" /></div>
          <div><div className="font-semibold">Stripe (payments)</div><div className="text-xs" style={{ color: "var(--text-soft)" }}>Customer payments gateway</div></div>
          <Badge variant="outline" className={`ml-auto ${stripeConnected ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "text-slate-400"}`}>
            {stripeConnected ? "Connected" : "Not configured"}
          </Badge>
        </div>
        <p className="text-sm" style={{ color: "var(--text-soft)" }}>Subscription billing live for Free / Pro / Scale tiers. Test card <code>4242 4242 4242 4242</code>. <Link to="/billing" className="underline" style={{ color: "var(--brand)" }}>Manage plans →</Link></p>
      </Card>

      {/* Twilio */}
      <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(239, 68, 68, 0.12)" }}><MessageSquare className="w-5 h-5 text-red-500" /></div>
          <div><div className="font-semibold">Twilio (SMS + Voice)</div><div className="text-xs" style={{ color: "var(--text-soft)" }}>Unlocks SMS + voice automation actions</div></div>
          <Badge variant="outline" className={`ml-auto ${twilio.connected ? "bg-emerald-50 border-emerald-200 text-emerald-700" : ""}`}>{twilio.connected ? "Connected" : "Not configured"}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Account SID</Label>
            <Input data-testid="twilio-sid" value={twilio.account_sid} onChange={(e) => setTwilio({ ...twilio, account_sid: e.target.value })} placeholder="AC…" />
          </div>
          <div>
            <Label>Auth Token</Label>
            <Input data-testid="twilio-token" type="password" value={twilio.auth_token} onChange={(e) => setTwilio({ ...twilio, auth_token: e.target.value })} placeholder="••••••••" />
          </div>
          <div>
            <Label>Sending phone</Label>
            <Input data-testid="twilio-phone" value={twilio.phone_number} onChange={(e) => setTwilio({ ...twilio, phone_number: e.target.value })} placeholder="+15555550100" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs" style={{ color: "var(--text-soft)" }}>Grab creds from <a className="underline" style={{ color: "var(--brand)" }} href="https://console.twilio.com/" target="_blank" rel="noreferrer">console.twilio.com</a> · your SID starts with <code>AC…</code></p>
          <Button data-testid="save-twilio" onClick={saveTwilio} className="text-white" style={{ background: "var(--brand)" }} disabled={loading}>Save</Button>
        </div>
      </Card>

      {/* Social */}
      <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(236, 72, 153, 0.12)" }}><Share2 className="w-5 h-5 text-pink-500" /></div>
          <div><div className="font-semibold">Social channels</div><div className="text-xs" style={{ color: "var(--text-soft)" }}>Cross-post from Social page once connected</div></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {socialCfg.map(({ key, label, Icon, hue }) => (
            <div key={key} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
              <div className="w-9 h-9 rounded flex items-center justify-center" style={{ background: `${hue}20` }}><Icon className="w-4 h-4" style={{ color: hue }} /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{label}</div>
                <div className="text-xs" style={{ color: "var(--text-soft)" }}>{socials[key] ? "Connected" : "Not connected"}</div>
              </div>
              {socials[key]
                ? <Button data-testid={`disconnect-${key}`} variant="outline" size="sm" onClick={() => disconnectSocial(key)}>Disconnect</Button>
                : <Button data-testid={`connect-${key}`} size="sm" className="text-white" style={{ background: hue }} onClick={() => connectSocial(key)}>Connect</Button>}
            </div>
          ))}
        </div>
        <p className="text-xs mt-3" style={{ color: "var(--text-soft)" }}>OAuth flow scaffolded — connects instantly in sandbox mode. Wire real platform tokens later to publish live.</p>
      </Card>
    </div>
  );
}

function StatusCard({ Icon, bg, title, sub, status, statusColor = "emerald", msg }) {
  const bgClass = { emerald: "bg-emerald-50", blue: "bg-blue-50", slate: "bg-slate-100" }[bg] || "bg-slate-100";
  const iconClass = { emerald: "text-emerald-600", blue: "text-[#0044FF]", slate: "text-slate-600" }[bg] || "text-slate-600";
  const badgeClass = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-700", amber: "bg-amber-50 border-amber-200 text-amber-700" }[statusColor] || "";
  return (
    <Card className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-md ${bgClass} flex items-center justify-center`}><Icon className={`w-5 h-5 ${iconClass}`} /></div>
        <div><div className="font-semibold">{title}</div><div className="text-xs" style={{ color: "var(--text-soft)" }}>{sub}</div></div>
        <Badge variant="outline" className={`ml-auto ${badgeClass}`}>{status}</Badge>
      </div>
      <p className="text-sm" style={{ color: "var(--text-soft)" }}>{msg}</p>
    </Card>
  );
}


function DomainVerification() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ sender_domain: "", sender_email: "", sender_name: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/domain/status");
      setStatus(data);
      setForm({ sender_domain: data.sender_domain || "", sender_email: data.sender_email || "", sender_name: data.sender_name || "" });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.sender_domain.trim()) { toast.error("Enter a domain"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/domain/setup", form);
      setStatus(data);
      toast.success("Domain saved — now add the DNS records below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/domain/verify");
      if (data.verified) toast.success(data.message);
      else toast.error(data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t).then(() => toast.success("Copied")); };

  if (loading) return <Card className="p-10 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "var(--brand)" }} /></Card>;

  return (
    <div className="space-y-6" data-testid="domain-tab">
      <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-5 h-5" style={{ color: "var(--brand)" }} />
          <h3 className="display text-xl">Send from your own domain</h3>
          {status?.verified && (
            <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200">
              <ShieldCheck className="w-3 h-3 mr-1" /> Verified {status.verified_at ? `· ${new Date(status.verified_at).toLocaleDateString()}` : ""}
            </Badge>
          )}
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--text-soft)" }}>
          Set your sending domain, then add the SPF / DKIM / DMARC DNS records at your registrar. Once verified, all outgoing campaigns will be signed from your domain — dramatically improving deliverability & branding.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>Sender domain</Label><Input data-testid="domain-input" value={form.sender_domain} onChange={(e) => setForm({ ...form, sender_domain: e.target.value })} placeholder="yourbrand.com" /></div>
          <div><Label>Sender email</Label><Input data-testid="sender-email-input" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="hello@yourbrand.com" /></div>
          <div><Label>Sender name</Label><Input data-testid="sender-name-input" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="Your Brand" /></div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button data-testid="domain-save" onClick={save} disabled={busy} className="text-white" style={{ background: "var(--brand)" }}>Save</Button>
          {status?.sender_domain && (
            <Button data-testid="domain-verify" onClick={verify} disabled={busy} variant="outline" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Verify DNS
            </Button>
          )}
        </div>
      </Card>

      {status?.records?.length > 0 && (
        <Card className="p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <h3 className="display text-lg mb-3">DNS records to add</h3>
          <p className="text-xs mb-4" style={{ color: "var(--text-soft)" }}>Log into your registrar (Namecheap, GoDaddy, Cloudflare, Hostinger, etc.) and add the records below to <strong>{status.sender_domain}</strong>. DNS propagation can take a few minutes to 24 hours.</p>
          <div className="space-y-3">
            {status.records.map((r, i) => (
              <div key={i} className="p-4 rounded-lg border" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid={`dns-record-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="mono">{r.type}</Badge>
                  <span className="text-xs mono" style={{ color: "var(--text-soft)" }}>{r.host}</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs p-2 rounded mono truncate" style={{ background: "var(--surface)", color: "var(--text)" }}>{r.value}</code>
                  <Button variant="ghost" size="icon" onClick={() => copyText(r.value)} data-testid={`copy-dns-${i}`}><Copy className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs mt-2" style={{ color: "var(--text-soft)" }}>{r.purpose}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
